/**
 * Error Message Sanitizer
 * Removes sensitive information from error messages before displaying to users
 */

/**
 * Patterns to detect and remove sensitive information
 */
const SENSITIVE_PATTERNS = [
  // Database connection strings
  /mongodb:\/\/[^\s]+/gi,
  /postgres:\/\/[^\s]+/gi,
  /mysql:\/\/[^\s]+/gi,
  /dynamodb[^\s]*/gi,
  
  // API keys and tokens
  /api[_-]?key[=:]\s*['"]?[\w-]+['"]?/gi,
  /token[=:]\s*['"]?[\w-]+['"]?/gi,
  /bearer\s+[\w-]+/gi,
  
  // File paths (absolute paths)
  /[a-z]:\\(?:[^\\/:*?"<>|\r\n]+\\)*[^\\/:*?"<>|\r\n]*/gi, // Windows paths
  /\/(?:home|usr|var|etc|opt)\/[^\s]*/gi, // Unix paths
  
  // IP addresses
  /\b(?:\d{1,3}\.){3}\d{1,3}\b/g,
  
  // Email addresses (in error messages)
  /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
  
  // Stack trace indicators
  /at\s+[^\s]+\s+\([^)]+\)/gi, // Match "at <anything> (<path>)"
  /^\s*at\s+.*/gm,
  
  // Function names with file paths
  /\w+\s*\([^)]*\.js:\d+:\d+\)/gi,
];

/**
 * Generic replacements for sensitive data
 */
const REPLACEMENTS = {
  connectionString: '[CONNECTION_STRING]',
  apiKey: '[API_KEY]',
  token: '[TOKEN]',
  filePath: '[PATH]',
  ipAddress: '[IP_ADDRESS]',
  email: '[EMAIL]',
  stackTrace: '[STACK_TRACE]',
};

/**
 * Sanitize an error message by removing sensitive information
 * @param {string|Error} error - Error message or Error object
 * @returns {string} Sanitized error message
 */
export const sanitizeErrorMessage = (error) => {
  if (!error) {
    return 'An unexpected error occurred';
  }

  // Extract message from Error object
  let message = typeof error === 'string' ? error : error.message || String(error);

  // Remove stack traces
  if (message.includes('\n    at ')) {
    message = message.split('\n')[0]; // Keep only the first line
  }

  // Apply pattern replacements
  message = message
    // Database connection strings
    .replace(/mongodb:\/\/[^\s]+/gi, REPLACEMENTS.connectionString)
    .replace(/postgres:\/\/[^\s]+/gi, REPLACEMENTS.connectionString)
    .replace(/mysql:\/\/[^\s]+/gi, REPLACEMENTS.connectionString)
    .replace(/dynamodb[^\s]*/gi, REPLACEMENTS.connectionString)
    
    // API keys and tokens
    .replace(/api[_-]?key[=:]\s*['"]?[\w-]+['"]?/gi, REPLACEMENTS.apiKey)
    .replace(/token[=:]\s*['"]?[\w-]+['"]?/gi, REPLACEMENTS.token)
    .replace(/bearer\s+[\w-]+/gi, REPLACEMENTS.token)
    
    // File paths
    .replace(/[a-z]:\\(?:[^\\/:*?"<>|\r\n]+\\)*[^\\/:*?"<>|\r\n]*/gi, REPLACEMENTS.filePath)
    .replace(/\/(?:home|usr|var|etc|opt)\/[^\s]*/gi, REPLACEMENTS.filePath)
    
    // IP addresses
    .replace(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g, REPLACEMENTS.ipAddress)
    
    // Stack traces
    .replace(/at\s+[^\s]+\s+\([^)]+\)/gi, REPLACEMENTS.stackTrace)
    .replace(/^\s*at\s+.*/gm, '')
    
    // Function names with file paths
    .replace(/\w+\s*\([^)]*\.js:\d+:\d+\)/gi, REPLACEMENTS.stackTrace);

  // Clean up multiple spaces and trim
  message = message.replace(/\s+/g, ' ').trim();

  // If message is empty after sanitization, return generic message
  if (!message || message.length === 0) {
    return 'An error occurred while processing your request';
  }

  // Ensure message doesn't exceed reasonable length
  const MAX_LENGTH = 200;
  if (message.length > MAX_LENGTH) {
    message = message.substring(0, MAX_LENGTH) + '...';
  }

  return message;
};

/**
 * Check if a string contains sensitive information
 * @param {string} text - Text to check
 * @returns {boolean} True if sensitive information is detected
 */
export const containsSensitiveInfo = (text) => {
  if (!text || typeof text !== 'string') {
    return false;
  }

  return SENSITIVE_PATTERNS.some(pattern => {
    // Reset regex lastIndex to avoid state issues
    pattern.lastIndex = 0;
    return pattern.test(text);
  });
};

/**
 * Create a user-friendly error message
 * @param {Error} error - Error object
 * @returns {string} User-friendly error message
 */
export const createUserFriendlyError = (error) => {
  const sanitized = sanitizeErrorMessage(error);
  
  // Map common error patterns to user-friendly messages
  const friendlyMessages = {
    'network': 'Unable to connect to the server. Please check your internet connection.',
    'timeout': 'The request took too long. Please try again.',
    'unauthorized': 'You are not authorized to perform this action.',
    'forbidden': 'Access to this resource is forbidden.',
    'not found': 'The requested resource was not found.',
    'server error': 'A server error occurred. Please try again later.',
    'bad request': 'The request was invalid. Please check your input.',
  };

  const lowerMessage = sanitized.toLowerCase();
  
  for (const [key, friendlyMsg] of Object.entries(friendlyMessages)) {
    if (lowerMessage.includes(key)) {
      return friendlyMsg;
    }
  }

  return sanitized;
};

export default {
  sanitizeErrorMessage,
  containsSensitiveInfo,
  createUserFriendlyError
};
